import { rdiffResult } from 'recursive-diff';
import { ThunkAction } from 'redux-thunk';
import { fetchDrugsLabTestsFromSymDia, fetchSymDiaFromSymDia } from '../../api';
import { SA } from '../../api/fetchAssessmentSummary';
import fetchComplimentaryDrugs, {
    ComplimentaryDrugResponse,
} from '../../api/fetchComplimentaryDrugs';
import fetchDrugsInteractions, {
    DrugForInteractions,
    DrugInteractionsResponse,
} from '../../api/fetchDrugsInteractions';
import { Drug, LabTest, Response } from '../../api/fetchDrugsLabTestsFromSymDia';
import { RelatedSymDiaResponse, RelatedSymDia } from '../../api/fetchSymDiaFromSymDia';
import fetchSymDiaProperties, {
    SymDiaProperties,
    PropertyGroupEntity,
} from '../../api/fetchSymDiaProperties';
import { GPTemplate } from '../db';
import { getPrescriptionsByVisitId, getGenieItemById } from '../idbHandler';
import { GlobalState } from '../reducers';
import {
    DrugInteractionException,
    PatientDetails,
    PatientDetailsVisits,
    RelatedProfileData,
    ToastType,
} from '../reducers/prescriptionPad';
import {
    AdvicesEntity,
    DiagnosisEntityOrSymptomsEntity,
    LabTestsEntityOrProceduresEntity,
    MedicationsEntity,
    ExaminationEntity,
    Prescription,
    VitalsEntity,
    Notes,
    Patient,
    Tool,
    Duration,
    LabRefer,
    LabVitalEntity,
    SelectionEntity,
    Attachment,
    DentalExamination,
    DentalProcedure,
    InjectionsEntity,
    ToolElements,
    ToolElement,
    IpdAdmission,
} from '../types/prescription';
import { ONE_CHAR_GENDER } from '../../pages/NewPatient';
import { CreateReferalData } from '../../api/createReferal';
import { Response as Draft } from '../../api/fetchPxDraft';
import { PadDForm, PrintConfig, Translate } from '../../api/pushVisit';
import { Actions } from '.';
import fetchSpecialityForDoctor, { Res } from '../../api/fetchSpecialityForDoctor';
import { track } from '../../tracking';
import { batch } from 'react-redux';
import fetchPastVitalsByPtOid, { PastVitalsData } from '../../api/fetchPastVitalsByPtOid';
import { DB_TABLES } from '../dbTables';
import {
    apiDrugToMedicationEntity,
    Inventory,
} from '../../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import {
    CurrentMedications,
    IVaccineStatus,
    MEDICAL_HISTORY_CATEGORY,
    MHItem2,
} from '../types/medicalHistory';
import { fetchAllVisits, fetchMR, getAllVisitsWithLinkedPatientIds } from '../../pages/utils';
import { createDBTool } from '../utils';
import { getGenieSetting } from '../selectors/session';
import { GeniePadElementsSettingItem } from '../../api/pushGenie';
import { captureError, captureErrorString } from '../../pages/captureError';
import { RequestResponse } from '../../api/fetchAllVisitsForPatient';
import { MED_TABLE_COL_QUANTITY_KEY } from '../../constants';
import { CodedMedicine, SubstitutesResponse } from '../../api/fetchDrugsSubstitute';
import { PxElSourceTrack } from '../../components/prescriptionPad/trackSource';
import { DoctorPackages } from '../../api/fetchDoctorPackages';
import moment from 'moment';
import { openNewMRNotification } from '../../components/common/openNewMRNotification';
import { ChartType } from '../../components/prescriptionPad/leftPanel/DentalChart/DentalChart';
import {
    Group,
    ProcessedDentalExaminations,
} from '../../components/prescriptionPad/leftPanel/DentalChart/DentalExamination';
import { IMilestoneHistory } from '../../components/common/types/milestones';
import { PatientHealthSummaryWithPoolingResponse } from '../../api/getPatientHealthSummaryWithPooling';
import { getDocIdForMdb, getMyDbId } from '../../api/mactiveid';
import { TreatmentPlan } from '../../api/getTreatmentPlans';
import { CareCanvas } from '../types/CareCanvas';
import { FETCH_STATE } from '../../components/common/PatientOverviewUtils';
import fetchDrugsByIds from '../../api/fetchDrugsByIds';

export type PrescriptionPadActions =
    | LoadPrescriptionAction
    | UpsertPrescriptionAction
    | UpdateVitalsAction
    | SetVitalsAction
    | DeleteVitalsAction
    | ClearVitalsAction
    | UpsertLabVitalAction
    | DeleteLabVitalAction
    | SetLabVitalsAction
    | UpsertSysptomAction
    | UpsertElementAction<ToolElement>
    | DeleteElementAction<ToolElement>
    | CopyKeyToAllAction<ToolElement>
    | DeleteSysptomAction
    | UpsertDyFormAction
    | DeleteDyFormAction
    | UpsertDiagnoseAction
    | DeleteDiagnoseAction
    | UpsertMedicationAction
    | DeleteMedicationAction
    | UpdateFollowupAction
    | UpdateSuggestedDrugsLabTestsAction
    | UpdateRelatedSymDiaAction
    | UpsertAdviceAction
    | DeleteAdviceAction
    | DeleteAdvicePermanentlyAction
    | EditAdviceAction
    | FinishPrescriptionAction
    | CreatePrescriptionTemplateAction
    | DeletePrescriptionTemplateAction
    | LoadPrescriptionTemplateAction
    | UpsertLabTestAction
    | DeleteLabTestAction
    | UpdateExaminationAction
    | UpdateExaminationFindingsAction
    | PushDiffAction
    | UndoAction
    | ClearPPAction
    | ClearPrescriptionPadAction
    | ShowDialogAction
    | HideDialogAction
    | ToggleUsePrescriptionTemplateAction
    | UpsertMedicalHistory
    | UpsertMedicalHistoryCardState
    | ToggleNoKnownMedicalHistory
    | MagicBoxActiveTab
    | DeleteMedicalHistory
    | UpdateAttachmentAction
    | ClearMedicalHistory
    | UpdateVitalsActionV0
    | UpdatePPNotes
    | UpdatePPPrivateNotes
    | UpdateDrugInteractionsAction
    | UpdateSymDiaPropertiesAction
    | UpdateSymDiaICDcodeAction
    | UpdateComplimentaryDrugAction
    | SetAssessmentSummaryAction
    | UpdateAssessmentSummaryFetchStateAction
    | UpdatePatientAction
    | UpdateReferalAction
    | UpdateLabReferalAction
    | ApplyDraftAction
    | SetToastTypeAction
    | CopyDurationInMedAction
    | CopyTestOnInLabInvestigationsAction
    | CopyRepeatOnInLabInvestigationsAction
    | CopySinceInSyAction
    | CopySinceInDyAction
    | SetFollowupAction
    | UpdateDraftIdAction
    | UdateTranslateAction
    | LoadTranslateAction
    | UpdateInventoryAction
    | UpdateDyFormPrintConfigAction
    | LoadPrintConfigAction
    | UpdatePrintConfigAction
    | SwapPxElementAction
    | UpdateLabsForReferalAction
    | UpdateLabToReferIdAction
    | UpdatePtPastVitals
    | UpsertPatientDetailsAction
    | SwapMedicalHistoryElementAction
    | CopyLabVitalDate
    | DownloadPtDirAction
    | SanitizePrescriptionAction
    | SanitizeFinalPrescriptionAction
    | UpdateMedicationsAction
    | UpdateLabTestsAction
    | FetchDrugsSubstituteAction
    | SetDrugsSubstituteAction
    | ReplaceHEMedicationAction
    | SetDoctorPackagesAction
    | UpsertDentalExaminationAction
    | DeleteDentalExaminationAction
    | DeleteDentalExaminationsAction
    | CopyDentalExaminationDateAction
    | EditDentalExaminationsGroupAction
    | UpsertDentalProcedureAction
    | UpsertDentalProceduresAction
    | DeleteDentalProcedureAction
    | CopyDentalProcedureDateAction
    | SetChartTypeAction
    | SetProcessedDentalExaminations
    | UpdateAdministeredVaccines
    | ClearAdministeredVaccines
    | SetPatientHealthSummaryAction
    | UpdateMarkedMilestones
    | SetMilestones
    | UpdateMilestone
    | SetInjectionElementsAction
    | UpsertInjectionAction
    | DeleteInjectionAction
    | SetOngoingTreatmentPlansAction
    | CopyOpFgpCopyFromAction
    | SetupPadGenieAutoSuggestAction
    | UpsertIpdAdmissionAction
    | UpsertCareCanvasAction
    | CopyCareCanvasAction
    | UpsertRelatedProfileDataAction
    | UpsertCurrentMedications
    | SetMedRouteEnabledAction;

export const LOAD_PRESCRIPTION = 'LOAD_PRESCRIPTION';
export const SANITIZE_PRESCRIPTION = 'SANITIZE_PRESCRIPTION';
export const UPSERT_PRESCRIPTION = 'UPSERT_PRESCRIPTION';
export const UPSERT_PRESCRIPTION_V2 = 'UPSERT_PRESCRIPTION_V2';
export const CLEAR_PRESCRIPTION = 'CLEAR_PRESCRIPTION';
export const CLEAR_PRESCRIPTION_PAD = 'CLEAR_PRESCRIPTION_PAD';
export const APPLY_DRAFT = 'APPLY_DRAFT';
export const SET_TOAST_TYPE = 'SET_TOAST_TYPE';
export const UPDATE_DRAFT_ID = 'UPDATE_DRAFT_ID';
export const COPY_DURATION_IN_MED = 'COPY_DURATION_IN_MED';
export const COPY_TEST_ON_LAB_INVESTIGATIONS = 'COPY_TEST_ON_LAB_INVESTIGATIONS';
export const COPY_REPEAT_ON_LAB_INVESTIGATIONS = 'COPY_REPEAT_ON_LAB_INVESTIGATIONS';
export const COPY_SINCE_IN_SY = 'COPY_SINCE_IN_SY';
export const COPY_SINCE_IN_DY = 'COPY_SINCE_IN_DY';
export const LOAD_TRANSLATE = 'LOAD_TRANSLATE';
export const UPDATE_TRANSLATE = 'UPDATE_TRANSLATE';
export const UPDATE_INVENTORY = 'UPDATE_INVENTORY';

export const UPDATE_VITALS = 'UPDATE_VITALS';
export const SET_VITALS = 'SET_VITALS';
export const DELETE_VITALS = 'DELETE_VITALS';
export const CLEAR_VITALS = 'CLEAR_VITALS';
export const UPDATE_VITALS_V0 = 'UPDATE_VITALS_V0';

export const SWAP_PX_ELEMENTS = 'SWAP_PX_ELEMENTS';
export const SWAP_MEDICAL_HISTORY_ELEMENTS = 'SWAP_MEDICAL_HISTORY_ELEMENTS';

export const UPSERT_SYMPTOM = 'UPSERT_SYMPTOM';
export const DELETE_SYMPTOM = 'DELETE_SYMPTOM';
export const OP_FGP_COPY_FROM = 'OP_FGP_COPY_FROM';

export const UPSERT_ELEMENT = 'UPSERT_ELEMENT';
export const DELETE_ELEMENT = 'DELETE_ELEMENT';
export const COPY_KEY_TO_ALL = 'COPY_KEY_TO_ALL';

export const DELETE_LAB_VITAL = 'DELETE_LAB_VITAL';
export const SET_LAB_VITALS = 'SET_LAB_VITALS';
export const UPSERT_LAB_VITAL = 'UPSERT_LAB_VITAL';
export const COPY_LAB_VITAL_DATE = 'COPY_LAB_VITAL_DATE';

export const UPSERT_DYFORM = 'UPSERT_DYFORM';
export const DELETE_DYFORM = 'DELETE_DYFORM';
export const UPDATE_PRINT_CONFIG = 'UPDATE_PRINT_CONFIG';
export const LOAD_PRINT_CONFIG = 'LOAD_PRINT_CONFIG';
export const UPDATE_DY_FORM_PRINT_CONFIG = 'UPDATE_DY_FORM_PRINT_CONFIG';

export const SHOW_DIALOG = 'SHOW_DIALOG';
export const HIDE_DIALOG = 'HIDE_DIALOG';

export const UPSERT_DIAGNOSE = 'UPSERT_DIAGNOSE';
export const DELETE_DIAGNOSE = 'DELETE_DIAGNOSE';

export const UPSERT_MEDICATION = 'UPSERT_MEDICATION';
export const UPSERT_INJECTION = 'UPSERT_INJECTION';
export const UPDATE_MEDICATIONS = 'UPDATE_MEDICATIONS';
export const DELETE_MEDICATION = 'DELETE_MEDICATION';
export const DELETE_INJECTION = 'DELETE_INJECTION';

export const UPSERT_LAB_TEST = 'UPSERT_LAB_TEST';
export const UPDATE_LAB_TESTS = 'UPDATE_LAB_TESTS';
export const DELETE_LAB_TEST = 'DELETE_LAB_TEST';

export const UPDATE_FOLLOWUP = 'UPDATE_FOLLOWUP';
export const SET_FOLLOWUP = 'SET_FOLLOWUP';
export const UPDATE_PATIENT = 'UPDATE_PATIENT';

export const UPDATE_REFERAL = 'UPDATE_REFERAL';
export const UPDATE_LAB_REFERAL = 'UPDATE_LAB_REFERAL';

export const UPDATE_SUGGESTED_DRUGS_LAB_TESTS = 'UPDATE_SUGGESTED_DRUGS_LAB_TESTS';
export const UPDATE_DRUG_INTERACTIONS = 'UPDATE_DRUG_INTERACTIONS';
export const UPDATE_COMPLIMENTARY_DRUG = 'UPDATE_COMPLIMENTARY_DRUG';
export const UPDATE_RELATED_SYM_DIA = 'UPDATE_RELATED_SYM_DIA';
export const UPDATE_SYM_DIA_PROPERTIES = 'UPDATE_SYM_DIA_PROPERTIES';
export const UPDATE_ICD_CODES = 'UPDATE_ICD_CODES';

export const FINISH_PRESCRIPTION = 'FINISH_PRESCRIPTION';

export const UPSERT_ADVICE = 'UPSERT_ADVICE';
export const DELETE_ADVICE = 'DELETE_ADVICE';
export const EDIT_ADVICE = 'EDIT_ADVICE';
export const DELETE_ADVICE_PERMANENTLY = 'DELETE_ADVICE_PERMANENTLY';

export const TOGGLE_USE_PRESCRIPTION_TEMPLATE_DIALOG = 'TOGGLE_USE_PRESCRIPTION_TEMPLATE_DIALOG';

export const CREATE_PRESCRIPTION_TEMPLATE = 'CREATE_PRESCRIPTION_TEMPLATE';
export const LOAD_PRESCRIPTION_TEMPLATE = 'LOAD_PRESCRIPTION_TEMPLATE';
export const DELETE_PRESCRIPTION_TEMPLATE = 'DELETE_PRESCRIPTION_TEMPLATE';

export const UPDATE_EXAMINATION = 'UPDATE_EXAMINATION';
export const UPDATE_PP_NOTES = 'UPDATE_PP_NOTES';
export const UPDATE_PP_PRIVATE_NOTES = 'UPDATE_PP_PRIVATE_NOTES';
export const UPDATE_EXAMINATION_FINDINGS = 'UPDATE_EXAMINATION_FINDINGS';

export const UPSERT_MEDICAL_HISTORY = 'UPSERT_MEDICAL_HISTORY';
export const UPSERT_ADMINISTERED_VACCINES = 'UPSERT_ADMINISTERED_VACCINES';
export const UPSERT_MILESTONES = 'UPSERT_MILESTONES';
export const UPDATE_MILESTONE = 'UPDATE_MILESTONE';
export const CLEAR_ADMINISTERED_VACCINES = 'CLEAR_ADMINISTERED_VACCINES';
export const SET_MILESTONES = 'SET_MILESTONES';
export const UPSERT_ATTACHMENT = 'UPSERT_ATTACHMENT';
export const DELETE_MEDICAL_HISTORY = 'DELETE_MEDICAL_HISTORY';
export const CLEAR_MEDICAL_HISTORY = 'CLEAR_MEDICAL_HISTORY';
export const UPSERT_MEDICAL_HISTORY_CARD_STATE = 'UPSERT_MEDICAL_HISTORY_CARD_STATE';
export const TOGGLE_NO_KNOWN_MEDICAL_HISTORY = 'TOGGLE_NO_KNOWN_MEDICAL_HISTORY';
export const MAGIC_BOX_ACTIVE_TAB = 'MAGIC_BOX_ACTIVE_TAB';
export const SET_ASSESSMENT_SUMMARY = 'SET_ASSESSMENT_SUMMARY';
export const UPDATE_ASSESSMENT_SUMMARY_FETCH_STATE = 'UPDATE_ASSESSMENT_SUMMARY_FETCH_STATE';

export const UPDATE_LABS_FOR_REFERAl = 'UPDATE_LABS_FOR_REFERAl';
export const UPDATE_LAB_TO_REFER_ID = 'UPDATE_LAB_TO_REFER_ID';

export const UPDATE_PT_PAST_VITALS = 'UPDATE_PT_PAST_VITALS';

export const UPSERT_PATIENT_DETAILS = 'UPSERT_PATIENT_DETAILS';

export const DOWNLOAD_PT_DIR = 'DOWNLOAD_PT_DIR';

export const SET_MED_ROUTE_ENABLED = 'SET_MED_ROUTE_ENABLED';

export const SANITIZE_FINAL_PRESCRIPTION = 'SANITIZE_FINAL_PRESCRIPTION';

export const FETCH_DRUGS_SUBSTITUTE = 'FETCH_DRUGS_SUBSTITUTE';
export const SET_DRUGS_SUBSTITUTE = 'SET_DRUGS_SUBSTITUTE';
export const REPLACE_HE_MEDICATION = 'REPLACE_HE_MEDICATION';

export const SET_DOCTOR_PACKAGES = 'SET_DOCTOR_PACKAGES';

export const UPSERT_DENTAL_EXAMINATION = 'UPSERT_DENTAL_EXAMINATION';
export const DELETE_DENTAL_EXAMINATION = 'DELETE_DENTAL_EXAMINATION';
export const DELETE_DENTAL_EXAMINATIONS = 'DELETE_DENTAL_EXAMINATIONS';
export const COPY_DENTAL_EXAMINATION_DATE = 'COPY_DENTAL_EXAMINATION_DATE';
export const EDIT_DENTAL_EXAMINATIONS_GROUP = 'EDIT_DENTAL_EXAMINATIONS_GROUP';

export const UPSERT_DENTAL_PROCEDURE = 'UPSERT_DENTAL_PROCEDURE';
export const DELETE_DENTAL_PROCEDURE = 'DELETE_DENTAL_PROCEDURE';
export const COPY_DENTAL_PROCEDURE_DATE = 'COPY_DENTAL_PROCEDURE_DATE';
export const UPSERT_DENTAL_PROCEDURES = 'UPSERT_DENTAL_PROCEDURES';

export const SET_CHART_TYPE = 'SET_CHART_TYPE';
export const SET_PROCESSED_DENTAL_EXAMINATIONS = 'SET_PROCESSED_DENTAL_EXAMINATIONS';

export const SET_PATIENT_HEALTH_SUMMARY = 'SET_PATIENT_HEALTH_SUMMARY';
export const SET_INJECTION_ELEMENTS = 'SET_INJECTION_ELEMENTS';

export const SET_ONGOING_TREATMENT_PLANS = 'SET_ONGOING_TREATMENT_PLANS';

export const SETUP_PAD_GENIE_AUTO_SUGGEST = 'SETUP_PAD_GENIE_AUTO_SUGGEST';

export const UPSERT_IPD_ADMISSION = 'UPSERT_IPD_ADMISSION';
export const UPSERT_CARE_CANVAS = 'UPSERT_CARE_CANVAS';
export const COPY_CARE_CANVAS = 'COPY_CARE_CANVAS';

export const UPSERT_RELATED_PROFILE_DATA = 'UPSERT_RELATED_PROFILE_DATA';

export const UPSERT_CURRENT_MEDICATIONS = 'UPSERT_CURRENT_MEDICATIONS';

export interface UpdatePrintConfigAction {
    type: typeof UPDATE_PRINT_CONFIG;
    printConfigs: PrintConfig;
}

export const updatePrintConfig = (printConfigs: PrintConfig): UpdatePrintConfigAction => ({
    type: UPDATE_PRINT_CONFIG,
    printConfigs,
});

export interface LoadPrintConfigAction {
    type: typeof LOAD_PRINT_CONFIG;
    printConfigs: PrintConfig;
}

export const loadPrintConfig = (printConfigs: PrintConfig): LoadPrintConfigAction => ({
    type: LOAD_PRINT_CONFIG,
    printConfigs,
});

export interface UpdateDyFormPrintConfigAction {
    type: typeof UPDATE_DY_FORM_PRINT_CONFIG;
    id: string;
    isPrintEnabled: boolean;
}

export const updateDyFormPrintConfig = (
    id: string,
    isPrintEnabled: boolean,
): UpdateDyFormPrintConfigAction => ({
    type: UPDATE_DY_FORM_PRINT_CONFIG,
    id,
    isPrintEnabled,
});

export interface UpsertMedicalHistory {
    type: typeof UPSERT_MEDICAL_HISTORY;
    cat: MEDICAL_HISTORY_CATEGORY;
    item: MHItem2;
    index?: number;
}

export interface DeleteMedicalHistory {
    type: typeof DELETE_MEDICAL_HISTORY;
    cat: MEDICAL_HISTORY_CATEGORY;
    itemId: string;
}

export const upsertMedicalHistory = (
    cat: MEDICAL_HISTORY_CATEGORY,
    item: MHItem2,
    index?: number,
): UpsertMedicalHistory => ({
    type: UPSERT_MEDICAL_HISTORY,
    cat,
    item,
    index,
});

export interface UpdateAttachmentAction {
    type: typeof UPSERT_ATTACHMENT;
    attachment?: Attachment;
}

export const updateAttachment = (attachment?: Attachment): UpdateAttachmentAction => ({
    type: UPSERT_ATTACHMENT,
    attachment,
});

export interface UpdateAdministeredVaccines {
    type: typeof UPSERT_ADMINISTERED_VACCINES;
    administeredVaccines?: IVaccineStatus[];
    allVaccines?: IVaccineStatus[];
}

export const updateAdministeredVaccines = (
    administeredVaccines?: IVaccineStatus[],
    allVaccines?: IVaccineStatus[],
): UpdateAdministeredVaccines => ({
    type: UPSERT_ADMINISTERED_VACCINES,
    administeredVaccines,
    allVaccines,
});

export interface UpdateMarkedMilestones {
    type: typeof UPSERT_MILESTONES;
    milestones?: IMilestoneHistory[];
    allMilestones?: IMilestoneHistory[];
}

export const updateMilestones = (
    milestones?: IMilestoneHistory[],
    allMilestones?: IMilestoneHistory[],
): UpdateMarkedMilestones => ({
    type: UPSERT_MILESTONES,
    milestones,
    allMilestones,
});

export interface UpdateMilestone {
    type: typeof UPDATE_MILESTONE;
    milestone?: IMilestoneHistory;
}

export const updateMilestone = (milestone?: IMilestoneHistory): UpdateMilestone => ({
    type: UPDATE_MILESTONE,
    milestone,
});

export interface ClearAdministeredVaccines {
    type: typeof CLEAR_ADMINISTERED_VACCINES;
    allVaccines?: IVaccineStatus[];
}

export const clearAdministeredVaccines = (
    allVaccines?: IVaccineStatus[],
): ClearAdministeredVaccines => ({
    type: CLEAR_ADMINISTERED_VACCINES,
    allVaccines,
});

export interface SetMilestones {
    type: typeof SET_MILESTONES;
    allMilestones?: IMilestoneHistory[];
    milestones?: IMilestoneHistory[];
}

export const setMilestones = (
    allMilestones: IMilestoneHistory[],
    milestones: IMilestoneHistory[],
): SetMilestones => ({
    type: SET_MILESTONES,
    allMilestones,
    milestones,
});

export interface UpsertMedicalHistoryCardState {
    type: typeof UPSERT_MEDICAL_HISTORY_CARD_STATE;
    cardState: boolean;
}

export const upsertMedicalHistoryCardState = (
    cardState: boolean,
): UpsertMedicalHistoryCardState => ({
    type: UPSERT_MEDICAL_HISTORY_CARD_STATE,
    cardState,
});

export interface ToggleNoKnownMedicalHistory {
    type: typeof TOGGLE_NO_KNOWN_MEDICAL_HISTORY;
    toggleState: boolean;
}

export const toggleNoKnownMedicalHistory = (toggleState: boolean): ToggleNoKnownMedicalHistory => ({
    type: TOGGLE_NO_KNOWN_MEDICAL_HISTORY,
    toggleState,
});

export interface MagicBoxActiveTab {
    type: typeof MAGIC_BOX_ACTIVE_TAB;
    activeTab: string;
}

export const updateMagicBoxActiveTab = (activeTab: string): MagicBoxActiveTab => ({
    type: MAGIC_BOX_ACTIVE_TAB,
    activeTab,
});

export const deleteMedicalHistory = (
    cat: MEDICAL_HISTORY_CATEGORY,
    itemId: string,
): DeleteMedicalHistory => ({
    type: DELETE_MEDICAL_HISTORY,
    cat,
    itemId,
});

export interface ClearMedicalHistory {
    type: typeof CLEAR_MEDICAL_HISTORY;
}

export const clearMedicalHistory = (): ClearMedicalHistory => ({
    type: CLEAR_MEDICAL_HISTORY,
});

export interface UpdateReferalAction {
    type: typeof UPDATE_REFERAL;
    refer?: CreateReferalData;
}

export const updateReferal = (refer?: CreateReferalData): UpdateReferalAction => {
    return { type: UPDATE_REFERAL, refer };
};

export interface UpdateLabReferalAction {
    type: typeof UPDATE_LAB_REFERAL;
    labRefer?: LabRefer;
}

export const updateLabReferal = (labRefer?: LabRefer): UpdateLabReferalAction => {
    return { type: UPDATE_LAB_REFERAL, labRefer };
};

export interface UpdatePatientAction {
    type: typeof UPDATE_PATIENT;
    patient: Patient;
    updatedRx?: Prescription;
}

export const updatePatient = (patient: Patient, updatedRx?: Prescription): UpdatePatientAction => {
    return { type: UPDATE_PATIENT, patient, updatedRx };
};

export interface ShowDialogAction {
    type: typeof SHOW_DIALOG;
    which:
        | 'exa'
        | 'vitals'
        | 'ph'
        | 'ppNotes'
        | 'referal'
        | 'summary'
        | 'lab-vital'
        | 'longitudinal-vital-view'
        | 'abdm-banner'
        | 'health-week'
        | 'substitutions'
        | 'support-tickets'
        | 'troubleshoot-my-system';
}

export const showDialog = (
    which:
        | 'exa'
        | 'vitals'
        | 'ph'
        | 'ppNotes'
        | 'referal'
        | 'summary'
        | 'lab-vital'
        | 'longitudinal-vital-view'
        | 'abdm-banner'
        | 'health-week'
        | 'substitutions'
        | 'support-tickets'
        | 'troubleshoot-my-system',
): ShowDialogAction => {
    return { type: SHOW_DIALOG, which };
};

export interface HideDialogAction {
    type: typeof HIDE_DIALOG;
    which:
        | 'exa'
        | 'vitals'
        | 'ph'
        | 'ppNotes'
        | 'referal'
        | 'summary'
        | 'lab-vital'
        | 'longitudinal-vital-view'
        | 'abdm-banner'
        | 'health-week'
        | 'substitutions'
        | 'support-tickets'
        | 'troubleshoot-my-system';
}

export const hideDialog = (
    which:
        | 'exa'
        | 'vitals'
        | 'ph'
        | 'ppNotes'
        | 'referal'
        | 'summary'
        | 'lab-vital'
        | 'longitudinal-vital-view'
        | 'abdm-banner'
        | 'health-week'
        | 'substitutions'
        | 'support-tickets'
        | 'troubleshoot-my-system',
): HideDialogAction => {
    return { type: HIDE_DIALOG, which };
};

export interface UpdateExaminationAction {
    type: typeof UPDATE_EXAMINATION;
    data: ExaminationEntity[];
}

export const updateExamination = (data: ExaminationEntity[]): UpdateExaminationAction => {
    return { type: UPDATE_EXAMINATION, data };
};

export interface UpdateExaminationFindingsAction {
    type: typeof UPDATE_EXAMINATION_FINDINGS;
    data: ExaminationEntity;
    index?: number;
}

export const updateExaminationFindings = (
    data: ExaminationEntity,
    index?: number,
): UpdateExaminationFindingsAction => {
    return { type: UPDATE_EXAMINATION_FINDINGS, data, index };
};

export interface SetAssessmentSummaryAction {
    type: typeof SET_ASSESSMENT_SUMMARY;
    data: SA;
}

export const setAssessmentSummary = (data: SA): SetAssessmentSummaryAction => {
    return { type: SET_ASSESSMENT_SUMMARY, data };
};

export interface UpdateAssessmentSummaryFetchStateAction {
    type: typeof UPDATE_ASSESSMENT_SUMMARY_FETCH_STATE;
    fetchState: 'loading' | 'error';
}

export const updateAssessmentSummaryFetchStateAction = (
    fetchState: 'loading' | 'error',
): UpdateAssessmentSummaryFetchStateAction => {
    return { type: UPDATE_ASSESSMENT_SUMMARY_FETCH_STATE, fetchState };
};

export interface UpdatePPNotes {
    type: typeof UPDATE_PP_NOTES;
    pNotes?: Notes;
}

export const updatePPNotes = (pNotes?: Notes): UpdatePPNotes => {
    return { type: UPDATE_PP_NOTES, pNotes };
};

export interface UpdatePPPrivateNotes {
    type: typeof UPDATE_PP_PRIVATE_NOTES;
    privateNotes?: Notes;
}

export const updatePPPrivateNotes = (privateNotes?: Notes): UpdatePPPrivateNotes => {
    return { type: UPDATE_PP_PRIVATE_NOTES, privateNotes };
};

export interface ToggleUsePrescriptionTemplateAction {
    type: typeof TOGGLE_USE_PRESCRIPTION_TEMPLATE_DIALOG;
    isVisible: boolean;
}

export const toggleUsePrescriptionTemplate = (
    isVisible: boolean,
): ToggleUsePrescriptionTemplateAction => {
    return { type: TOGGLE_USE_PRESCRIPTION_TEMPLATE_DIALOG, isVisible };
};

export const PUSH_DIFF = 'PUSH_DIFF';
export const UNDO_ACTION = 'UNDO_ACTION';

export interface UndoAction {
    type: typeof UNDO_ACTION;
}

export const undo = (): UndoAction => {
    return { type: UNDO_ACTION };
};

// TODO
export interface ClearPrescriptionPadAction {
    type: typeof CLEAR_PRESCRIPTION_PAD;
}

export const clearPrescriptionPad = (): ClearPrescriptionPadAction => {
    return { type: CLEAR_PRESCRIPTION_PAD };
};

// TODO
export interface ClearPPAction {
    type: typeof CLEAR_PRESCRIPTION;
}

export const clearPP = (): ClearPPAction => {
    return { type: CLEAR_PRESCRIPTION };
};

export interface ApplyDraftAction {
    type: typeof APPLY_DRAFT;
    draft: Draft;
    padVisits?: RequestResponse;
    treatmentProcedures?: DentalProcedure[];
}

export const applyDraft = (
    draft: Draft,
    padVisits?: RequestResponse,
    treatmentProcedures?: DentalProcedure[],
): ApplyDraftAction => {
    return { type: APPLY_DRAFT, draft, padVisits, treatmentProcedures };
};

export interface UpdateDraftIdAction {
    type: typeof UPDATE_DRAFT_ID;
    draftId: string;
}

export const updateDraftId = (draftId: string): UpdateDraftIdAction => {
    return { type: UPDATE_DRAFT_ID, draftId };
};

export interface CopyDurationInMedAction {
    type: typeof COPY_DURATION_IN_MED;
    duration?: Duration;
    reCalculateQt?: boolean;
}

export interface CopyTestOnInLabInvestigationsAction {
    type: typeof COPY_TEST_ON_LAB_INVESTIGATIONS;
    test_on: string;
}

export const copyTestOnInLabInvestigations = (
    test_on: string,
): CopyTestOnInLabInvestigationsAction => {
    return { type: COPY_TEST_ON_LAB_INVESTIGATIONS, test_on };
};

export interface CopyRepeatOnInLabInvestigationsAction {
    type: typeof COPY_REPEAT_ON_LAB_INVESTIGATIONS;
    repeat_on: string;
}

export const copyRepeatOnInLabInvestigations = (
    repeat_on: string,
): CopyRepeatOnInLabInvestigationsAction => {
    return { type: COPY_REPEAT_ON_LAB_INVESTIGATIONS, repeat_on };
};

export const copyDurationToAllMed = (
    duration?: Duration,
): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch, getState): Promise<void> => {
        const medElements = getGenieSetting(
            getState(),
            'medElements',
        ) as GeniePadElementsSettingItem[];
        const qtColumnEnabled = medElements.find((ele) => ele.id === MED_TABLE_COL_QUANTITY_KEY)
            ?.isShown;
        dispatch(copyDurationInMed(duration, qtColumnEnabled));
    };
};

export const copyDurationInMed = (
    duration?: Duration,
    reCalculateQt?: boolean,
): CopyDurationInMedAction => {
    return { type: COPY_DURATION_IN_MED, duration, reCalculateQt };
};

export interface CopySinceInSyAction {
    type: typeof COPY_SINCE_IN_SY;
    since: SelectionEntity | Duration;
}

export const copySinceInSy = (since: SelectionEntity | Duration): CopySinceInSyAction => {
    return { type: COPY_SINCE_IN_SY, since };
};

export interface CopySinceInDyAction {
    type: typeof COPY_SINCE_IN_DY;
    since: SelectionEntity | Duration;
}

export const copySinceInDy = (since: SelectionEntity | Duration): CopySinceInDyAction => {
    return { type: COPY_SINCE_IN_DY, since };
};

export interface UdateTranslateAction {
    type: typeof UPDATE_TRANSLATE;
    translate: Translate;
}

export const updateTranslate = (translate: Translate): UdateTranslateAction => {
    return { type: UPDATE_TRANSLATE, translate };
};

export type OP_FGP_COPY_FROM = 'dsr' | 'usr' | 'cgp' | 'dar' | 'uar';

export interface CopyOpFgpCopyFromAction {
    type: typeof OP_FGP_COPY_FROM;
    from: OP_FGP_COPY_FROM;
}

export const copyOpFgpCopyFrom = (from: OP_FGP_COPY_FROM): CopyOpFgpCopyFromAction => {
    return { type: OP_FGP_COPY_FROM, from };
};

export interface LoadTranslateAction {
    type: typeof LOAD_TRANSLATE;
    translate: Translate;
}

export const loadTranslate = (translate: Translate): LoadTranslateAction => {
    return { type: LOAD_TRANSLATE, translate };
};

export interface UpdateInventoryAction {
    type: typeof UPDATE_INVENTORY;
    inventory: Inventory;
}

export const updateInventory = (inventory: Inventory): UpdateInventoryAction => {
    return {
        type: UPDATE_INVENTORY,
        inventory,
    };
};

export interface SetToastTypeAction {
    type: typeof SET_TOAST_TYPE;
    toast: ToastType;
}

export const setToastType = (toast: ToastType): SetToastTypeAction => {
    return { type: SET_TOAST_TYPE, toast };
};

export interface PushDiffAction {
    type: typeof PUSH_DIFF;
    diff: rdiffResult[];
}

export const pushDiff = (diff: rdiffResult[]): PushDiffAction => {
    return { type: PUSH_DIFF, diff };
};

export interface UpsertAdviceAction {
    type: typeof UPSERT_ADVICE;
    advice: AdvicesEntity;
}

export const upsertAdvice = (advice: AdvicesEntity): UpsertAdviceAction => {
    return { type: UPSERT_ADVICE, advice };
};

export interface DeleteAdviceAction {
    type: typeof DELETE_ADVICE;
    id: string;
}

export const deleteAdvice = (id: string): DeleteAdviceAction => {
    return { type: DELETE_ADVICE, id };
};

export interface DeleteAdvicePermanentlyAction {
    type: typeof DELETE_ADVICE_PERMANENTLY;
    text: string;
    id: string;
}

export const deleteAdvicePermanently = (
    text: string,
    id: string,
): DeleteAdvicePermanentlyAction => {
    return { type: DELETE_ADVICE_PERMANENTLY, text, id };
};

export interface EditAdviceAction {
    type: typeof EDIT_ADVICE;
    advice: AdvicesEntity;
    oldAdvice?: AdvicesEntity;
}

export const editAdvices = (advice: AdvicesEntity, oldAdvice?: AdvicesEntity): EditAdviceAction => {
    return { type: EDIT_ADVICE, advice, oldAdvice };
};

export interface FinishPrescriptionAction {
    type: typeof FINISH_PRESCRIPTION;
    px?: Prescription;
}

export const finishPrescription = (px?: Prescription): FinishPrescriptionAction => {
    return { type: FINISH_PRESCRIPTION, px };
};

export interface CreatePrescriptionTemplateAction {
    type: typeof CREATE_PRESCRIPTION_TEMPLATE;
    entity: PX_TEMPLATE_ENTITY;
    name: string;
    id?: string;
    data?: Tool;
    isPackage?: boolean;
}

export interface DeletePrescriptionTemplateAction {
    type: typeof DELETE_PRESCRIPTION_TEMPLATE;
    entity: PX_TEMPLATE_ENTITY;
    id: string;
}

export type PX_TEMPLATE_ENTITY = 'PX' | keyof Tool;

export const createPrescriptionTemplate = (
    entity: PX_TEMPLATE_ENTITY,
    name: string,
    id?: string,
    data?: Tool,
    isPackage?: boolean,
): CreatePrescriptionTemplateAction => {
    return { type: CREATE_PRESCRIPTION_TEMPLATE, entity, id, name, data, isPackage };
};

export const deletePrescriptionTemplate = (
    entity: PX_TEMPLATE_ENTITY,
    id: string,
): DeletePrescriptionTemplateAction => {
    return { type: DELETE_PRESCRIPTION_TEMPLATE, entity, id };
};

export interface LoadPrescriptionAction {
    type: typeof LOAD_PRESCRIPTION;
    prescription: Prescription;
    translate?: Translate;
    filters?: { [k: string]: string };
}

export const loadPrescription = (
    prescription: Prescription,
    translate?: Translate,
    filters?: { [k: string]: string },
): LoadPrescriptionAction => {
    return { type: LOAD_PRESCRIPTION, prescription, translate, filters };
};

export interface SanitizePrescriptionAction {
    type: typeof SANITIZE_PRESCRIPTION;
    medElements: GeniePadElementsSettingItem[];
}

export const sanitizePrescription = (
    medElements: GeniePadElementsSettingItem[],
): SanitizePrescriptionAction => {
    return { type: SANITIZE_PRESCRIPTION, medElements };
};

export interface UpsertPrescriptionAction {
    type: typeof UPSERT_PRESCRIPTION | typeof UPSERT_PRESCRIPTION_V2;
    prescriptionElements: Partial<Prescription>;
}

/**
 * @deprecated This should be namaed as setPrescription
 */
export const upsertPrescription = (
    prescriptionElements: Partial<Prescription>,
): UpsertPrescriptionAction => {
    return { type: UPSERT_PRESCRIPTION, prescriptionElements };
};

export const upsertPrescriptionV2 = (
    prescriptionElements: Partial<Prescription>,
): UpsertPrescriptionAction => {
    return { type: UPSERT_PRESCRIPTION_V2, prescriptionElements };
};

export interface SanitizeFinalPrescriptionAction {
    type: typeof SANITIZE_FINAL_PRESCRIPTION;
    px: Prescription;
}

export const sanitizeFinalPrescription = (px: Prescription): SanitizeFinalPrescriptionAction => {
    return { type: SANITIZE_FINAL_PRESCRIPTION, px };
};

export type Vitals = { [key: string]: VitalsEntity };

export interface UpdateVitalsAction {
    type: typeof UPDATE_VITALS;
    vitals: VitalsEntity;
    index?: number;
}

export const updateVitals = (vitals: VitalsEntity, index?: number): UpdateVitalsAction => {
    return { type: UPDATE_VITALS, vitals, index };
};

export interface SetVitalsAction {
    type: typeof SET_VITALS;
    vitals: VitalsEntity[];
}

export const setVitals = (vitals: VitalsEntity[]): SetVitalsAction => {
    return { type: SET_VITALS, vitals };
};

export interface DeleteVitalsAction {
    type: typeof DELETE_VITALS;
    id: string;
    index?: number;
    isDelFromGenie?: boolean;
}

export const deleteVitals = (
    id: string,
    index?: number,
    isDelFromGenie?: boolean,
): DeleteVitalsAction => {
    return { type: DELETE_VITALS, id, index, isDelFromGenie };
};

export interface ClearVitalsAction {
    type: typeof CLEAR_VITALS;
    deletedVitals?: string[];
}

export const clearVitals = (deletedVitals?: string[]): ClearVitalsAction => {
    return { type: CLEAR_VITALS, deletedVitals };
};
export interface SwapPxElementAction {
    type: typeof SWAP_PX_ELEMENTS;
    what: keyof Tool;
    from: number;
    to: number;
}

export const swapPxElement = (what: keyof Tool, from: number, to: number): SwapPxElementAction => {
    return { type: SWAP_PX_ELEMENTS, what, from, to };
};

export interface SwapMedicalHistoryElementAction {
    type: typeof SWAP_MEDICAL_HISTORY_ELEMENTS;
    what: MEDICAL_HISTORY_CATEGORY;
    from: number;
    to: number;
}

export const swapMedicalHistoryElement = (
    what: MEDICAL_HISTORY_CATEGORY,
    from: number,
    to: number,
): SwapMedicalHistoryElementAction => {
    return { type: SWAP_MEDICAL_HISTORY_ELEMENTS, what, from, to };
};

export interface UpsertSysptomAction {
    type: typeof UPSERT_SYMPTOM;
    sysptom: DiagnosisEntityOrSymptomsEntity;
    index?: number;
}

export const upsertSymptom = (
    sysptom: DiagnosisEntityOrSymptomsEntity,
    index?: number,
): UpsertSysptomAction => {
    return { type: UPSERT_SYMPTOM, sysptom, index };
};

export interface UpsertElementAction<T> {
    type: typeof UPSERT_ELEMENT;
    key: keyof ToolElements;
    value: T;
    index?: number;
    insert?: boolean;
}

export const upsertElement = <T>(
    key: keyof ToolElements,
    value: T,
    index?: number,
    insert?: boolean,
): UpsertElementAction<T> => {
    return { type: UPSERT_ELEMENT, key, value, index, insert };
};

export interface DeleteElementAction<T> {
    type: typeof DELETE_ELEMENT;
    key: keyof ToolElements;
    index: number;
}

export const deleteElement = <T>(
    key: keyof ToolElements,
    index: number,
): DeleteElementAction<T> => {
    return { type: DELETE_ELEMENT, key, index };
};

export interface CopyKeyToAllAction<T> {
    type: typeof COPY_KEY_TO_ALL;
    key: keyof ToolElements;
    ele_key: string;
    ele_value: string;
}

export const copyKeyToAll = <T>(
    key: keyof ToolElements,
    ele_key: string,
    ele_value: string,
): CopyKeyToAllAction<T> => {
    return { type: COPY_KEY_TO_ALL, key, ele_key, ele_value };
};

export interface UpsertLabVitalAction {
    type: typeof UPSERT_LAB_VITAL;
    element: LabVitalEntity;
    index?: number;
}

export const upsertLabVital = (element: LabVitalEntity, index?: number): UpsertLabVitalAction => {
    return { type: UPSERT_LAB_VITAL, element, index };
};

export const copyLabVitalDate = (date: string): CopyLabVitalDate => {
    return { type: COPY_LAB_VITAL_DATE, date };
};

export interface CopyLabVitalDate {
    type: typeof COPY_LAB_VITAL_DATE;
    date: string;
}

export interface UpsertDyFormAction {
    type: typeof UPSERT_DYFORM;
    dyform: PadDForm;
}

export const upsertDyForm = (dyform: PadDForm): UpsertDyFormAction => {
    return { type: UPSERT_DYFORM, dyform };
};

export interface DeleteDyFormAction {
    type: typeof DELETE_DYFORM;
    id: string;
}

export const deleteDyForm = (id: string): DeleteDyFormAction => {
    return { type: DELETE_DYFORM, id };
};

export interface DeleteSysptomAction {
    type: typeof DELETE_SYMPTOM;
    sysptomId: string;
}

export const deleteSysptom = (sysptomId: string): DeleteSysptomAction => {
    return { type: DELETE_SYMPTOM, sysptomId };
};

export interface DeleteLabVitalAction {
    type: typeof DELETE_LAB_VITAL;
    id: string;
    index?: number;
}

export const deleteLabVital = (id: string, index?: number): DeleteLabVitalAction => {
    return { type: DELETE_LAB_VITAL, id, index };
};

export interface SetLabVitalsAction {
    type: typeof SET_LAB_VITALS;
    labVitals: LabVitalEntity[];
}

export const setLabVitals = (labVitals: LabVitalEntity[]): SetLabVitalsAction => {
    return { type: SET_LAB_VITALS, labVitals };
};

export interface UpsertDiagnoseAction {
    type: typeof UPSERT_DIAGNOSE;
    diagnose: DiagnosisEntityOrSymptomsEntity;
    index?: number;
}

export const upsertDiagnose = (
    diagnose: DiagnosisEntityOrSymptomsEntity,
    index?: number,
): UpsertDiagnoseAction => {
    return { type: UPSERT_DIAGNOSE, diagnose, index };
};

export interface DeleteDiagnoseAction {
    type: typeof DELETE_DIAGNOSE;
    diagnoseId: string;
}

export const deleteDiagnose = (diagnoseId: string): DeleteDiagnoseAction => {
    return { type: DELETE_DIAGNOSE, diagnoseId };
};

export interface UpsertMedicationAction {
    type: typeof UPSERT_MEDICATION;
    medication: MedicationsEntity | MedicationsEntity[];
    index?: number;
    _isNewMedIntroduced?: boolean; // Used to track if a new medication is introduced for post-processing purposes
}

export const upsertMedication = (
    medication: MedicationsEntity | MedicationsEntity[],
    index?: number,
): UpsertMedicationAction => {
    return { type: UPSERT_MEDICATION, medication, index };
};

export interface UpdateMedicationsAction {
    type: typeof UPDATE_MEDICATIONS;
    medications: {
        [k: string]: Partial<MedicationsEntity>;
    };
}

export const updateMedications = (medications: {
    [k: string]: Partial<MedicationsEntity>;
}): UpdateMedicationsAction => {
    return { type: UPDATE_MEDICATIONS, medications };
};

export interface DeleteMedicationAction {
    type: typeof DELETE_MEDICATION;
    medicationId: string;
}

export const deleteMedication = (medicationId: string): DeleteMedicationAction => {
    return { type: DELETE_MEDICATION, medicationId };
};

export interface UpsertLabTestAction {
    type: typeof UPSERT_LAB_TEST;
    labTest: LabTestsEntityOrProceduresEntity;
    index?: number;
    doApiCall?: boolean;
}

export const upsertLabTest = (
    labTest: LabTestsEntityOrProceduresEntity,
    index?: number,
    doApiCall?: boolean,
): UpsertLabTestAction => {
    return { type: UPSERT_LAB_TEST, labTest, index, doApiCall };
};

export interface UpdateLabTestsAction {
    type: typeof UPDATE_LAB_TESTS;
    labTests: {
        [k: string]: Partial<LabTestsEntityOrProceduresEntity>;
    };
}

export const updateLabTests = (labTests: {
    [k: string]: Partial<LabTestsEntityOrProceduresEntity>;
}): UpdateLabTestsAction => {
    return { type: UPDATE_LAB_TESTS, labTests };
};

export interface DeleteLabTestAction {
    type: typeof DELETE_LAB_TEST;
    id: string;
}

export const deleteLabTest = (id: string): DeleteLabTestAction => {
    return { type: DELETE_LAB_TEST, id };
};

export interface UpdateFollowupAction {
    type: typeof UPDATE_FOLLOWUP;
    date?: string;
    optionId?: string;
    notes?: string;
}

export const updateFollowup = (
    date?: string,
    optionId?: string,
    notes?: string,
): UpdateFollowupAction => {
    return { type: UPDATE_FOLLOWUP, date, optionId, notes };
};

export interface SetFollowupAction {
    type: typeof SET_FOLLOWUP;
    date?: string;
    optionId?: string;
    notes?: string;
}

export const setFollowup = (
    date?: string,
    optionId?: string,
    notes?: string,
): SetFollowupAction => {
    return { type: SET_FOLLOWUP, date, optionId, notes };
};

export interface UpdateSuggestedDrugsLabTestsAction {
    type: typeof UPDATE_SUGGESTED_DRUGS_LAB_TESTS;
    drugs: Drug[];
    labTests: LabTest[];
}

export interface UpdateDrugInteractionsAction {
    type: typeof UPDATE_DRUG_INTERACTIONS;
    interactions: DrugInteractionException[];
}

export const updateDrugInteractions = (
    interactions: DrugInteractionException[],
): UpdateDrugInteractionsAction => {
    return { type: UPDATE_DRUG_INTERACTIONS, interactions };
};

export interface UpdateComplimentaryDrugAction {
    type: typeof UPDATE_COMPLIMENTARY_DRUG;
    complimentaryDrugs: ComplimentaryDrugResponse;
}

export const updateComplimentaryDrug = (
    complimentaryDrugs: ComplimentaryDrugResponse,
): UpdateComplimentaryDrugAction => {
    return { type: UPDATE_COMPLIMENTARY_DRUG, complimentaryDrugs };
};

export interface UpdateRelatedSymDiaAction {
    type: typeof UPDATE_RELATED_SYM_DIA;
    symptoms: RelatedSymDia[];
    diagnoses: RelatedSymDia[];
}

export const updateSuggestedDrugsLabTests = (
    drugs: Drug[],
    labTests: LabTest[],
): UpdateSuggestedDrugsLabTestsAction => {
    return { type: UPDATE_SUGGESTED_DRUGS_LAB_TESTS, drugs, labTests };
};

export const updateRelatedSymDia = (
    symptoms: RelatedSymDia[],
    diagnoses: RelatedSymDia[],
): UpdateRelatedSymDiaAction => {
    return { type: UPDATE_RELATED_SYM_DIA, symptoms, diagnoses };
};

export interface UpdateSymDiaPropertiesAction {
    type: typeof UPDATE_SYM_DIA_PROPERTIES;
    symDiaProperties: { [k: string]: PropertyGroupEntity[] };
}

export const updateSymDiaProperties = (symDiaProperties: {
    [k: string]: PropertyGroupEntity[];
}): UpdateSymDiaPropertiesAction => {
    return { type: UPDATE_SYM_DIA_PROPERTIES, symDiaProperties };
};

export interface UpdateSymDiaICDcodeAction {
    type: typeof UPDATE_ICD_CODES;
    symDiaICDCodes: {
        [k: string]: {
            icd10_code: string;
            icd10_name: string;
        };
    };
}

export const updateSymDiaIcdProperties = (symDiaICDCodes: {
    [k: string]: {
        icd10_code: string;
        icd10_name: string;
    };
}): UpdateSymDiaICDcodeAction => {
    return { type: UPDATE_ICD_CODES, symDiaICDCodes };
};

export interface UpdateLabsForReferalAction {
    type: typeof UPDATE_LABS_FOR_REFERAl;
    labs: Res;
}

export const updateLabsForReferal = (labs: Res): UpdateLabsForReferalAction => {
    return { type: UPDATE_LABS_FOR_REFERAl, labs };
};

export interface UpdateLabToReferIdAction {
    type: typeof UPDATE_LAB_TO_REFER_ID;
    id?: string;
}

export const updateLabToReferId = (id?: string): UpdateLabToReferIdAction => {
    return { type: UPDATE_LAB_TO_REFER_ID, id };
};

export interface UpdatePtPastVitals {
    type: typeof UPDATE_PT_PAST_VITALS;
    ptPastVitals: PastVitalsData;
}

export const updatePtPastVitals = (ptPastVitals: PastVitalsData): UpdatePtPastVitals => {
    return { type: UPDATE_PT_PAST_VITALS, ptPastVitals };
};
export interface DownloadPtDirAction {
    type: typeof DOWNLOAD_PT_DIR;
}

export const downloadPtDir = (): DownloadPtDirAction => {
    return { type: DOWNLOAD_PT_DIR };
};

export interface SetMedRouteEnabledAction {
    type: typeof SET_MED_ROUTE_ENABLED;
    medRouteEnabled: boolean;
}

export const setMedRouteEnabled = (medRouteEnabled: boolean): SetMedRouteEnabledAction => {
    return { type: SET_MED_ROUTE_ENABLED, medRouteEnabled };
};

export const fetchAndCacheLabsForReferal = (): ThunkAction<
    Promise<void>,
    GlobalState,
    null,
    Actions
> => {
    return async (dispatch, getState): Promise<void> => {
        try {
            const res = await fetchSpecialityForDoctor();

            if (res.success) {
                batch(() => {
                    const labs = res.lab || [];
                    dispatch(updateLabsForReferal(res));

                    const selectedLab = getState().prescriptionPad.prescription?.labRefer?.lab;

                    dispatch(
                        updateLabToReferId(
                            (selectedLab && selectedLab.length > 0 && selectedLab[0]._id) ||
                                (labs?.length > 0 &&
                                    labs[0].network &&
                                    labs[0].network.length > 0 &&
                                    labs[0].network[0]._id) ||
                                undefined,
                        ),
                    );
                });
                return;
            }
        } catch (e) {
            console.error('fetchAndCacheLabsForReferal=>', e);
            let message = 'Unknown Error';
            if (e instanceof Error) message = e.message;
            track('ERROR', { on: 'fetchAndCacheLabsForReferal', what: message });
        }
    };
};

export interface UpsertPatientDetailsAction {
    type: typeof UPSERT_PATIENT_DETAILS;
    details: PatientDetails;
}

export const upsertPatientDetails = (details: PatientDetails): UpsertPatientDetailsAction => {
    return { type: UPSERT_PATIENT_DETAILS, details };
};

export interface UpsertRelatedProfileDataAction {
    type: typeof UPSERT_RELATED_PROFILE_DATA;
    pid: string;
    details: RelatedProfileData;
}

export const upsertRelatedProfileData = (
    pid: string,
    details: RelatedProfileData,
): UpsertRelatedProfileDataAction => {
    return { type: UPSERT_RELATED_PROFILE_DATA, pid, details };
};

export interface UpsertCurrentMedications {
    type: typeof UPSERT_CURRENT_MEDICATIONS;
    currentMedications: CurrentMedications[];
}

export const upsertCurrentMedications = (
    currentMedications: CurrentMedications[],
): UpsertCurrentMedications => {
    return { type: UPSERT_CURRENT_MEDICATIONS, currentMedications };
};

const getAllVisitsAndPxFromIDB = async (id: string) => {
    const allVisits = await (await getAllVisitsWithLinkedPatientIds(id)).filter(
        // TODO move this filter to idb index
        (t) => t.status === 'CM',
    );
    const allPx =
        (await getPrescriptionsByVisitId(...allVisits.map((t) => t.id)))?.sort((a, b) => {
            return b.date.getTime() - a.date.getTime();
        }) || [];
    const data = allPx.filter(Boolean).map((t) => ({
        ...createDBTool(t),
        date: t.date.toISOString(),
        visitId: t.visitId,
        patient: t.patient,
        precriptionId: t.id,
        flavour: 'DW',
        type: t.type,
        doc_name: t.doc_name,
    }));
    return { allVisits, allPx, data };
};

// trigger this action on complete of api data fetch in upload mh
export const cachePatientDetails = (
    id: string,
    avoidLocalVisitFetch?: boolean,
    avoidVisitsFetch?: boolean,
    pxId?: string,
): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        track('cachePatientDetails', {
            id,
            avoidLocalVisitFetch: avoidLocalVisitFetch?.toString(),
            avoidVisitsFetch: avoidVisitsFetch?.toString(),
        });

        let visitsData: PatientDetailsVisits | undefined;

        if (!(avoidLocalVisitFetch && avoidVisitsFetch)) {
            const { allVisits, allPx, data } = await getAllVisitsAndPxFromIDB(id);

            if (!avoidLocalVisitFetch) {
                dispatch(
                    upsertPatientDetails({
                        visits: {
                            state: FETCH_STATE.SUCCESS,
                            data: data,
                        },
                    }),
                );
            } else {
                track('AVOIDED_LOCAL_VISIT_FETCH', {
                    ptId: id,
                });
            }

            if (!avoidVisitsFetch) {
                try {
                    const response = await fetchAllVisits(id);
                    visitsData = response;

                    if (response?.state === 'success' && response?.data) {
                        track('MAGIC_BOX_VISITS', {
                            lv: allVisits.length.toString(),
                            lp: allPx.length.toString(),
                            bv: response.data.length.toString(),
                            ptId: id,
                        });

                        dispatch(
                            upsertPatientDetails({
                                visits: response,
                            }),
                        );
                    } else {
                        captureErrorString(
                            'failed to fetchAllVisits: ' +
                                id +
                                ' response:' +
                                JSON.stringify(response),
                        );
                        track('MAGIC_BOX_VISITS_FAILED', {
                            response: JSON.stringify(response),
                        });
                    }
                } catch (e) {
                    console.error('cachePatientDetails=>', e);
                    captureError(e as Error);
                    track('MAGIC_BOX_VISITS_EXCEPTION', { error: (e as Error).message });
                }
            } else {
                track('AVOIDED_ALL_VISIT_FETCH', {
                    ptId: id,
                });
            }
        } else {
            track('AVOIDED_LOAD_VISIT_FETCH', {
                ptId: id,
            });
        }

        fetchMR(id, undefined, undefined, true)
            .then((data) => {
                if (data?.fetchState === 'success' && data.medicalRecords) {
                    if (pxId && visitsData && data.medicalRecords) {
                        const lastVisit = visitsData?.data?.sort((date1, date2) => {
                            return date1 > date2 ? -1 : 1;
                        })[0];

                        if (lastVisit) {
                            const newMedRecordsSince5Days = data.medicalRecords?.filter((m) =>
                                moment(moment.unix(m.u_at).toISOString()).isSameOrAfter(
                                    moment(lastVisit?.date),
                                ),
                            ).length;

                            if (newMedRecordsSince5Days && newMedRecordsSince5Days !== 0) {
                                openNewMRNotification(newMedRecordsSince5Days, pxId);
                            }
                        }
                    }

                    dispatch(
                        upsertPatientDetails({
                            mr: {
                                state: FETCH_STATE.SUCCESS,
                                data: data.medicalRecords,
                                lastUpdatedAt: new Date().toISOString(),
                            },
                            allPtIds: data.allPtIds,
                        }),
                    );
                }
            })
            .catch((e) => {
                console.error('cachePatientDetails=>', e);
            });
    };
};

export const fetchAndCachePtVitals = (
    ptId?: string,
): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        if (!ptId) {
            return;
        }
        try {
            const res = await fetchPastVitalsByPtOid(ptId);

            if (res.status === 'success') {
                batch(() => {
                    dispatch(updatePtPastVitals(res.data.vitalData));
                });
                return;
            }
        } catch (e) {
            console.error('fetchAndCachePtVitals=>', e);
            let message = 'Unknown Error';
            if (e instanceof Error) message = e.message;
            track('ERROR', { on: 'fetchAndCachePtVitals', what: message });
        }
    };
};

export const cacheDrugsLabTestsFromSymDia = (
    symIds: string,
    diaIds: string,
): ThunkAction<
    Promise<Response[] | void>,
    GlobalState,
    null,
    UpdateSuggestedDrugsLabTestsAction
> => {
    return (dispatch, getState): Promise<Response[] | void> => {
        if (!getState().session.smartSuggestions) {
            dispatch(updateSuggestedDrugsLabTests([], []));
            return Promise.resolve();
        }
        return fetchDrugsLabTestsFromSymDia(symIds, diaIds)
            .then((res: Response) => {
                dispatch(updateSuggestedDrugsLabTests(res.drugs, res.lab_tests));
            })
            .catch((e) => {
                console.error('fetchDrugsLabTestsFromSymDia =>', e);
            });
    };
};

export const cacheDrugInteractions = (
    drugs: DrugForInteractions[],
): ThunkAction<
    Promise<DrugInteractionsResponse[] | void>,
    GlobalState,
    null,
    UpdateDrugInteractionsAction
> => {
    return (dispatch, getState): Promise<DrugInteractionsResponse[] | void> => {
        if (!getState().session.smartSuggestions) {
            dispatch(updateDrugInteractions([]));
            return Promise.resolve();
        }
        const docid = getDocIdForMdb() || '';
        return fetchDrugsInteractions(docid, drugs)
            .then((res: DrugInteractionsResponse) => {
                if (!res) {
                    dispatch(updateDrugInteractions([]));
                    return;
                }

                const prescription: Prescription | undefined = getState().prescriptionPad
                    .prescription;

                if (!prescription) {
                    dispatch(updateDrugInteractions([]));
                    return;
                }

                const { medications } = prescription;

                if (!medications) {
                    dispatch(updateDrugInteractions([]));
                    return;
                }

                const exceptions: DrugInteractionException[] = [];
                const interaction = res?.interaction || {};
                Object.keys(interaction).forEach((key: string) => {
                    interaction[key].forEach((es: string[]) => {
                        let allDrugPresent = true;
                        const exDrugs: { id: string; name: string; generic: string }[] = [];
                        for (let i = 0; i < es.length; i++) {
                            const drug = medications.find((m) => m.generic_id === es[i]);

                            if (drug) {
                                exDrugs.push({
                                    id: drug.id,
                                    name: drug.name,
                                    generic: drug.generic_name || '',
                                });
                            } else {
                                allDrugPresent = false;
                                break;
                            }
                        }

                        if (allDrugPresent) {
                            exceptions.push({
                                type: key,
                                drugs: exDrugs,
                            });
                        }
                    });
                });

                dispatch(updateDrugInteractions(exceptions));
            })
            .catch((e) => {
                console.error('fetchDrugsInteractions =>', e);
            });
    };
};

export const fetchAndUpdateLatestDrugInfo = (
    drugsIds: string[],
): ThunkAction<Promise<void>, GlobalState, null, UpdateMedicationsAction> => {
    return (dispatch, getState): Promise<void> => {
        if (!getState().prescriptionPad.prescription) {
            // no more on rx Pad
            return Promise.resolve();
        }
        return fetchDrugsByIds(drugsIds)
            .then((res) => {
                if (!res) {
                    return;
                }

                const prescription: Prescription | undefined = getState().prescriptionPad
                    .prescription;

                if (!prescription) {
                    return;
                }

                const { medications: rxMedications } = prescription;

                if (!rxMedications?.length) {
                    return;
                }

                const updatedMeds: MedicationsEntity[] = rxMedications.map((med) => ({
                    ...med,
                    ...(res[med.id] ? apiDrugToMedicationEntity(res[med.id]) : {}),
                    generic_id: res[med.id]?.generic_id || med.generic_id,
                    generic_name: res[med.id]?.generic_name || med.generic_name,
                    name: med.actual_name ? med.name : res[med.id]?.name || med.name,

                    // to keep the existing values as in, might get overwritten by apiDrugToMedicationEntity
                    dose: med.dose,
                    frequency: med.frequency,
                    duration: med.duration,
                    instructions: med.instruction,
                    timing: med.timing,
                    start_from: med.start_from,
                    quantity: med.quantity,
                }));

                const map: { [k: string]: MedicationsEntity } = {};
                updatedMeds.forEach((med) => {
                    map[med.id] = med;
                });

                dispatch(updateMedications(map));
            })
            .catch((e) => {
                console.error('fetchDrugsInteractions =>', e);
            });
    };
};

export const cacheComplimentaryDrugs = (
    drugIds: string,
): ThunkAction<
    Promise<ComplimentaryDrugResponse[] | void>,
    GlobalState,
    null,
    UpdateComplimentaryDrugAction
> => {
    return (dispatch, getState): Promise<ComplimentaryDrugResponse[] | void> => {
        if (!getState().session.smartSuggestions) {
            dispatch(updateComplimentaryDrug([]));
            return Promise.resolve();
        }
        return fetchComplimentaryDrugs(drugIds)
            .then((res: ComplimentaryDrugResponse) => {
                if (!res) {
                    dispatch(updateComplimentaryDrug([]));
                    return;
                }

                const prescription: Prescription | undefined = getState().prescriptionPad
                    .prescription;

                if (!prescription) {
                    dispatch(updateComplimentaryDrug([]));
                    return;
                }

                dispatch(updateComplimentaryDrug(res));
            })
            .catch((e) => {
                console.error('fetchDrugsInteractions =>', e);
            });
    };
};

export const cacheRelatedSymDiaFromSymDia = (
    symIds: string,
    diaIds: string,
): ThunkAction<
    Promise<RelatedSymDiaResponse | void>,
    GlobalState,
    null,
    UpdateRelatedSymDiaAction
> => {
    return (dispatch, getState): Promise<RelatedSymDiaResponse | void> => {
        const state: GlobalState = getState();
        if (!getState().session.smartSuggestions) {
            dispatch(updateRelatedSymDia([], []));
            return Promise.resolve();
        }
        const gender = state.prescriptionPad.prescription?.patient.profile.personal.gender
            ? ONE_CHAR_GENDER[
                  state.prescriptionPad.prescription?.patient.profile.personal.gender.toLowerCase()
              ]
            : undefined;
        return fetchSymDiaFromSymDia(symIds, diaIds, gender)
            .then((res: RelatedSymDiaResponse) => {
                dispatch(updateRelatedSymDia(res.symptoms || [], res.diagnoses || []));
            })
            .catch((e) => {
                console.error('fetchSymDiaFromSymDia =>', e);
            });
    };
};
export const cacheSymDiaProperties = (
    symIds: string,
    diaIds: string,
): ThunkAction<
    Promise<SymDiaProperties[] | void>,
    GlobalState,
    null,
    UpdateSymDiaPropertiesAction | UpdateSymDiaICDcodeAction
> => {
    return (dispatch): Promise<SymDiaProperties[] | void> => {
        return fetchSymDiaProperties(symIds, diaIds)
            .then((res: SymDiaProperties[]) => {
                if (!res) {
                    return;
                }
                const map: { [k: string]: PropertyGroupEntity[] } = {};
                const icdMap: {
                    [key: string]: {
                        icd10_code: string;
                        icd10_name: string;
                    };
                } = {};
                res.forEach((pg) => {
                    if (pg.propertyGroup) map[pg.id] = pg.propertyGroup;
                    if (pg.icd10_code)
                        icdMap[pg.id] = {
                            icd10_code: pg.icd10_code,
                            icd10_name: pg.icd10_name,
                        };
                });
                dispatch(updateSymDiaProperties(map));
                dispatch(updateSymDiaIcdProperties(icdMap));
            })
            .catch((e) => {
                console.error('fetchSymDiaFromSymDia =>', e);
            });
    };
};

export interface LoadPrescriptionTemplateAction {
    type: typeof LOAD_PRESCRIPTION_TEMPLATE;
    entity: PX_TEMPLATE_ENTITY;
    pt: GPTemplate;
    medElements: GeniePadElementsSettingItem[];
}

export const loadPrescriptionTemplate = (
    entity: PX_TEMPLATE_ENTITY,
    pt: GPTemplate,
    medElements: GeniePadElementsSettingItem[],
): LoadPrescriptionTemplateAction => {
    return { type: LOAD_PRESCRIPTION_TEMPLATE, pt, entity, medElements };
};

type ToolWithPx = Tool & { PX: string };
type PadElements = Partial<ToolWithPx>;

const TEMPLATE_MAP: { [key in keyof PadElements]: DB_TABLES } = {
    PX: DB_TABLES['prescriptionTemplates'],
    labTests: DB_TABLES['gLTTemplates'],
    medications: DB_TABLES['gMedTemplates'],
    labVitals: DB_TABLES['gLabVitalsTemplates'],
    examinations: DB_TABLES['gExaminationsTemplates'],
    diagnosis: DB_TABLES['gDiaTemplates'],
    symptoms: DB_TABLES['gSymTemplates'],
    injections: DB_TABLES['gInjectionTemplates'],
    procedures: DB_TABLES['gProceduresTemplates'],
};

// TODO write tests
export const applyPrescriptionTemplate = (
    entity: PX_TEMPLATE_ENTITY,
    id: string,
): ThunkAction<Promise<Drug[] | void>, GlobalState, null, LoadPrescriptionTemplateAction> => {
    return (dispatch, getState): Promise<Drug[] | void> => {
        const table: DB_TABLES | undefined = TEMPLATE_MAP[entity];

        const state: GlobalState = getState();
        const medElements = getGenieSetting(state, 'medElements') as GeniePadElementsSettingItem[];

        if (!table) return Promise.reject(new Error('table not found'));

        return getGenieItemById(getMyDbId() || '', table, id)
            .then((pt) => {
                if (pt) {
                    dispatch(loadPrescriptionTemplate(entity, pt, medElements));
                } else {
                    console.error('getPrescriptionTemplateById =>', 'not found');
                }
            })
            .catch((e) => {
                console.error('getPrescriptionTemplateById =>', e);
            });
    };
};

export interface UpdateVitalsActionV0 {
    type: typeof UPDATE_VITALS_V0;
    vitals: Vitals;
}

export const updateVitalsV0 = (vitals: Vitals): UpdateVitalsActionV0 => {
    return { type: UPDATE_VITALS_V0, vitals };
};

export interface FetchDrugsSubstituteAction {
    type: typeof FETCH_DRUGS_SUBSTITUTE;
}

export const fetchDrugsSubstitute = (): FetchDrugsSubstituteAction => {
    return { type: FETCH_DRUGS_SUBSTITUTE };
};

export interface SetDrugsSubstituteAction {
    type: typeof SET_DRUGS_SUBSTITUTE;
    substitutes?: SubstitutesResponse;
}

export const setDrugsSubstitute = (substitutes?: SubstitutesResponse): SetDrugsSubstituteAction => {
    return { type: SET_DRUGS_SUBSTITUTE, substitutes };
};

export interface ReplaceHEMedicationAction {
    type: typeof REPLACE_HE_MEDICATION;
    substitutes: { [k: string]: CodedMedicine & PxElSourceTrack };
}

export const replaceHEMedication = (substitutes: {
    [k: string]: CodedMedicine & PxElSourceTrack;
}): ReplaceHEMedicationAction => {
    return { type: REPLACE_HE_MEDICATION, substitutes };
};

export interface SetDoctorPackagesAction {
    type: typeof SET_DOCTOR_PACKAGES;
    doctorPackages?: DoctorPackages;
}

export const setDoctorPackages = (doctorPackages?: DoctorPackages): SetDoctorPackagesAction => {
    return { type: SET_DOCTOR_PACKAGES, doctorPackages };
};

export interface UpsertDentalExaminationAction {
    type: typeof UPSERT_DENTAL_EXAMINATION;
    dentalExamination: DentalExamination;
    index?: number;
    bypass?: boolean;
}

export const upsertDentalExamination = (
    dentalExamination: DentalExamination,
    index?: number,
    bypass?: boolean,
): UpsertDentalExaminationAction => {
    return { type: UPSERT_DENTAL_EXAMINATION, dentalExamination, index, bypass };
};

export interface DeleteDentalExaminationAction {
    type: typeof DELETE_DENTAL_EXAMINATION;
    id: string;
    index?: number;
    group?: Group;
}

export const deleteDentalExamination = (
    id: string,
    index?: number,
    group?: Group,
): DeleteDentalExaminationAction => {
    return { type: DELETE_DENTAL_EXAMINATION, id, index, group };
};

export interface DeleteDentalExaminationsAction {
    type: typeof DELETE_DENTAL_EXAMINATIONS;
    teeth_id: string;
    group?: Group;
}

export const deleteDentalExaminations = (
    teeth_id: string,
    group?: Group,
): DeleteDentalExaminationsAction => {
    return { type: DELETE_DENTAL_EXAMINATIONS, teeth_id, group };
};

export const copyDentalExaminationDate = (
    teeth_id: string,
    since: string,
    group?: Group,
): CopyDentalExaminationDateAction => {
    return { type: COPY_DENTAL_EXAMINATION_DATE, since, teeth_id, group };
};

export interface CopyDentalExaminationDateAction {
    type: typeof COPY_DENTAL_EXAMINATION_DATE;
    since: string;
    teeth_id: string;
    group?: Group;
}

export const editDentalExaminationsGroup = (
    old_group_id: string,
    new_group_id: string,
): EditDentalExaminationsGroupAction => {
    return { type: EDIT_DENTAL_EXAMINATIONS_GROUP, old_group_id, new_group_id };
};

export interface EditDentalExaminationsGroupAction {
    type: typeof EDIT_DENTAL_EXAMINATIONS_GROUP;
    old_group_id: string;
    new_group_id: string;
}

export interface UpsertDentalProcedureAction {
    type: typeof UPSERT_DENTAL_PROCEDURE;
    dentalProcedure: DentalProcedure;
    index?: number;
}

export const upsertDentalProcedure = (
    dentalProcedure: DentalProcedure,
    index?: number,
): UpsertDentalProcedureAction => {
    return { type: UPSERT_DENTAL_PROCEDURE, dentalProcedure, index };
};

export interface UpsertDentalProceduresAction {
    type: typeof UPSERT_DENTAL_PROCEDURES;
    dentalProcedures: DentalProcedure[];
}

export const upsertDentalProcedures = (
    dentalProcedures: DentalProcedure[],
): UpsertDentalProceduresAction => {
    return { type: UPSERT_DENTAL_PROCEDURES, dentalProcedures };
};

export interface DeleteDentalProcedureAction {
    type: typeof DELETE_DENTAL_PROCEDURE;
    id: string;
    index?: number;
}

export const deleteDentalProcedure = (id: string, index?: number): DeleteDentalProcedureAction => {
    return { type: DELETE_DENTAL_PROCEDURE, id, index };
};

export const copyDentalProcedureDate = (
    start_on: string,
    service_id?: string,
): CopyDentalProcedureDateAction => {
    return { type: COPY_DENTAL_PROCEDURE_DATE, start_on, service_id };
};

export interface CopyDentalProcedureDateAction {
    type: typeof COPY_DENTAL_PROCEDURE_DATE;
    start_on: string;
    service_id?: string;
}

export const setChartType = (chartType: ChartType): SetChartTypeAction => {
    return { type: SET_CHART_TYPE, chartType };
};

export interface SetChartTypeAction {
    type: typeof SET_CHART_TYPE;
    chartType: ChartType;
}

export const setProcessedDentalExaminations = (
    processedDentalExaminations: ProcessedDentalExaminations[],
): SetProcessedDentalExaminations => {
    return { type: SET_PROCESSED_DENTAL_EXAMINATIONS, processedDentalExaminations };
};

export interface SetProcessedDentalExaminations {
    type: typeof SET_PROCESSED_DENTAL_EXAMINATIONS;
    processedDentalExaminations: ProcessedDentalExaminations[];
}

export const setPatientHealthSummary = (
    patientHealthSummary?: PatientHealthSummaryWithPoolingResponse,
): SetPatientHealthSummaryAction => {
    return { type: SET_PATIENT_HEALTH_SUMMARY, patientHealthSummary };
};

export interface SetPatientHealthSummaryAction {
    type: typeof SET_PATIENT_HEALTH_SUMMARY;
    patientHealthSummary?: PatientHealthSummaryWithPoolingResponse;
}

export const setInjectionElements = (
    injectionElements: InjectionsEntity[],
): SetInjectionElementsAction => {
    return { type: SET_INJECTION_ELEMENTS, injectionElements };
};

export interface SetInjectionElementsAction {
    type: typeof SET_INJECTION_ELEMENTS;
    injectionElements?: InjectionsEntity[];
}

export const upsertInjection = (
    injection: InjectionsEntity,
    index?: number,
): UpsertInjectionAction => {
    return { type: UPSERT_INJECTION, injection, index };
};

export interface UpsertInjectionAction {
    type: typeof UPSERT_INJECTION;
    injection: InjectionsEntity;
    index?: number;
}

export const deleteInjection = (injection: InjectionsEntity): DeleteInjectionAction => {
    return { type: DELETE_INJECTION, injection };
};

export interface DeleteInjectionAction {
    type: typeof DELETE_INJECTION;
    injection: InjectionsEntity;
}
export interface SetOngoingTreatmentPlansAction {
    type: typeof SET_ONGOING_TREATMENT_PLANS;
    ongoingTreatmentPlans: TreatmentPlan[];
}

export const setOngoingTreatmentPlans = (
    ongoingTreatmentPlans: TreatmentPlan[],
): SetOngoingTreatmentPlansAction => {
    return { type: SET_ONGOING_TREATMENT_PLANS, ongoingTreatmentPlans };
};

export interface SetupPadGenieAutoSuggestAction {
    type: typeof SETUP_PAD_GENIE_AUTO_SUGGEST;
}

export const setupPadGenieAutoSuggest = (): SetupPadGenieAutoSuggestAction => {
    return { type: SETUP_PAD_GENIE_AUTO_SUGGEST };
};

export interface UpsertIpdAdmissionAction {
    type: typeof UPSERT_IPD_ADMISSION;
    ipdAdmission: IpdAdmission;
}

export const upsertIpdAdmission = (ipdAdmission: IpdAdmission): UpsertIpdAdmissionAction => {
    return { type: UPSERT_IPD_ADMISSION, ipdAdmission };
};

export interface UpsertCareCanvasAction {
    type: typeof UPSERT_CARE_CANVAS;
    careCanvas?: CareCanvas[];
}

export const upsertCareCanvas = (careCanvas?: CareCanvas[]): UpsertCareCanvasAction => {
    return { type: UPSERT_CARE_CANVAS, careCanvas };
};

export interface CopyCareCanvasAction {
    type: typeof COPY_CARE_CANVAS;
    careCanvas?: CareCanvas[];
}

export const copyCareCanvas = (careCanvas?: CareCanvas[]): CopyCareCanvasAction => {
    return { type: COPY_CARE_CANVAS, careCanvas };
};

export const cacheRelatedPatientPastVisits = (
    id?: string,
): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        if (!id) {
            track('ERROR', {
                on: 'cacheRelatedPatientPastVisits',
                what: 'Patient id is missing',
            });
            return;
        }
        dispatch(
            upsertRelatedProfileData(id, {
                visits: {
                    state: FETCH_STATE.FETCHING,
                },
            }),
        );
        const { data } = await getAllVisitsAndPxFromIDB(id);
        dispatch(
            upsertRelatedProfileData(id, {
                visits: {
                    state: FETCH_STATE.SUCCESS,
                    data: data,
                },
            }),
        );
        try {
            const response = await fetchAllVisits(id);
            if (response?.state === 'success' && response?.data) {
                dispatch(
                    upsertRelatedProfileData(id, {
                        visits: response,
                    }),
                );
            } else {
                dispatch(
                    upsertRelatedProfileData(id, {
                        visits: {
                            state: FETCH_STATE.ERROR,
                        },
                    }),
                );
                track('ERROR', {
                    on: 'cacheRelatedPatientPastVisits',
                    what: `Error in fetchAllVisits for ${id}`,
                });
            }
        } catch (e) {
            dispatch(
                upsertRelatedProfileData(id, {
                    visits: {
                        state: FETCH_STATE.ERROR,
                    },
                }),
            );
            let message = 'Unknown Error';
            if (e instanceof Error) message = e.message;
            track('ERROR', { on: 'cacheRelatedPatientPastVisits', what: message });
        }
    };
};

export const cacheRelatedPatientMedicalRecords = (
    id?: string,
): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        if (!id) {
            track('ERROR', {
                on: 'cacheRelatedPatientMedicalRecords',
                what: 'Patient id is missing',
            });
            return;
        }
        dispatch(
            upsertRelatedProfileData(id, {
                mr: {
                    state: FETCH_STATE.FETCHING,
                },
            }),
        );
        try {
            const data = await fetchMR(id);
            if (data?.fetchState === 'success' && data.medicalRecords) {
                dispatch(
                    upsertRelatedProfileData(id, {
                        mr: {
                            state: FETCH_STATE.SUCCESS,
                            data: data.medicalRecords,
                            lastUpdatedAt: new Date().toISOString(),
                        },
                        allPtIds: data.allPtIds,
                    }),
                );
            } else {
                dispatch(
                    upsertRelatedProfileData(id, {
                        mr: {
                            state: FETCH_STATE.ERROR,
                        },
                    }),
                );
                track('ERROR', {
                    on: 'cacheRelatedPatientMedicalRecords',
                    what: `Error in fetchMR for ${id}`,
                });
            }
        } catch (e) {
            dispatch(
                upsertRelatedProfileData(id, {
                    mr: {
                        state: FETCH_STATE.ERROR,
                    },
                }),
            );
            let message = 'Unknown Error';
            if (e instanceof Error) message = e.message;
            track('ERROR', { on: 'cacheRelatedPatientMedicalRecords', what: message });
        }
    };
};

export const cacheRelatedPatientPastVitals = (
    id?: string,
): ThunkAction<Promise<void>, GlobalState, null, Actions> => {
    return async (dispatch): Promise<void> => {
        if (!id) {
            track('ERROR', { on: 'cacheRelatedPatientPastVitals', what: 'Patient id is missing' });
            return;
        }
        try {
            dispatch(
                upsertRelatedProfileData(id, {
                    pastVitals: {
                        state: FETCH_STATE.FETCHING,
                    },
                }),
            );
            const res = await fetchPastVitalsByPtOid(id);
            if (res.status === 'success') {
                dispatch(
                    upsertRelatedProfileData(id, {
                        pastVitals: {
                            state: FETCH_STATE.SUCCESS,
                            data: res.data.vitalData,
                        },
                    }),
                );
            } else {
                dispatch(
                    upsertRelatedProfileData(id, {
                        pastVitals: {
                            state: FETCH_STATE.ERROR,
                        },
                    }),
                );
                track('ERROR', {
                    on: 'cacheRelatedPatientPastVitals',
                    what: `Error in fetchPastVitalsByPtOid for ${id}`,
                });
            }
        } catch (e) {
            dispatch(
                upsertRelatedProfileData(id, {
                    pastVitals: {
                        state: FETCH_STATE.ERROR,
                    },
                }),
            );
            let message = 'Unknown Error';
            if (e instanceof Error) message = e.message;
            track('ERROR', { on: 'cacheRelatedPatientPastVitals', what: message });
        }
    };
};
